/** @format */

import { AttributesResolvedSkuInventoryService } from './attributes-resolved-sku-inventory.service';
import { inject, Injectable } from '@angular/core';
import { ProductSku } from '@shared/data-access/interfaces';
import { Store } from '@ngrx/store';
import { selectSelectedAttributeOptions } from '../+state/attributes.selectors';
import { setResolvedSku } from '@pdp/data-access/state';
import { take } from 'rxjs/operators';

@Injectable({
	providedIn: 'root'
})
export class AttributesResolvedSkuService {
	private readonly store = inject(Store);
	private readonly attributesResolvedSkuInventoryService = inject(
		AttributesResolvedSkuInventoryService
	);

	currentlySetResolvedSku = '';

	/**
	 *  @description Get All Selected Options in state and format to a object to compare to skus defining attributes
	 * to find a sku that has all matching defining attributes.
	 */
	checkForResolvedSku(
		availableSkusForSelection: ProductSku[],
		skuDefiningAttributesLength: number
	) {
		let resolvedSku;
		let doAllAttributesHaveSelectedOption = false;
		this.store
			.select(selectSelectedAttributeOptions)
			.pipe(take(1))
			.subscribe((attributeOptions) => {
				if (attributeOptions.length === skuDefiningAttributesLength) {
					doAllAttributesHaveSelectedOption = true;
					const attributeNameValueObject = {};
					attributeOptions.forEach((attributeOption) => {
						attributeNameValueObject[attributeOption.name] = attributeOption.value;
					});
					resolvedSku = this.getResolvedSku(
						availableSkusForSelection,
						attributeNameValueObject
					);
				} else {
					resolvedSku = null;
				}
			});

		if (doAllAttributesHaveSelectedOption || this.currentlySetResolvedSku !== '') {
			//Set resolved sku and resolved sku inventory in state
			this.setResolvedSku(resolvedSku);
			this.attributesResolvedSkuInventoryService.setResolvedSkuInventoryInState(
				resolvedSku?.partNumber
			);
		}
	}

	/**
	 *  @description filter Out Skus that do not have matching defining attributes. If all match set that as resolved sku.
	 *  reset resolved sku if user clicks on disabled attribute.
	 * @param availableSkusForSelection
	 * @param attributeNameValueObject example {size: 'xl', color: 'red', width: 'medium'}
	 */
	getResolvedSku(
		availableSkusForSelection: ProductSku[],
		attributeNameValueObject: { [key: string]: string }
	): ProductSku | null {
		return availableSkusForSelection.find((sku) => {
			let invalidAttributes = [];
			if (sku.definingAttributes) {
				// checking each defining attribute against selections
				invalidAttributes = sku.definingAttributes.filter((attr) => {
					const selectedValue = attributeNameValueObject[attr.name];
					if (selectedValue && attr.value.toLowerCase() !== selectedValue.toLowerCase()) {
						return true;
					}
					return false;
				});
			}
			return invalidAttributes.length <= 0;
		});
	}

	setResolvedSku(resolvedSku: ProductSku): void {
		// Only set resolved sku if it is different the the current one in state
		if (this.currentlySetResolvedSku !== resolvedSku?.partNumber) {
			this.currentlySetResolvedSku = resolvedSku?.partNumber;
			this.store.dispatch(setResolvedSku({ resolvedSku }));
		}
	}
}
